import React, { useRef, useEffect, useState } from 'react'
import gsap, { Power4 } from 'gsap'
import { Box, List, ListItem, Typography } from '@mui/material'

import { SectionDivider } from './SectionDivider'

import Image from '@components/Image'

type OurLogosProps = {
  data: {
    customHeaderText: string
    ourlogotitle: string
    outlogoimage: { _key: string; asset: { _ref: string } }[]
  }
}

export const OurLogoV2 = ({ data }: OurLogosProps) => {
  const [isNotDesktop, setIsNotDesktop] = useState(false)

  const logoRefs = useRef([])
  const timelineDelay = 1

  useEffect(() => {
    const resizeListener = () => {
      setIsNotDesktop(window.matchMedia('(max-width: 1024px)').matches)
    }

    resizeListener()
    window.addEventListener('resize', resizeListener)

    let timeline: gsap.core.Timeline | null
    const logo: never[] = []

    if (!isNotDesktop) {
      const animateLogos = () => {
        if (logoRefs.current[0] === null) return

        data.outlogoimage?.forEach((_logoimage, i) => {
          logo.push(logoRefs.current[i])
        })

        if (timeline) {
          timeline.clear()
          timeline.kill()

          timeline
            .to(
              logo[0],
              {
                opacity: 0,
                yPercent: -100
              },
              `+=${timelineDelay}`
            )
            .to(
              logo[5],
              {
                yPercent: -100
              },
              '<'
            )
            .set(logo[0], {
              yPercent: 100,
              opacity: 1
            })

          timeline
            .to(
              logo[2],
              {
                opacity: 0,
                yPercent: -100
              },
              `+=${timelineDelay}`
            )
            .to(
              logo[7],
              {
                yPercent: -100
              },
              '<'
            )
            .set(logo[2], {
              yPercent: 100,
              opacity: 1
            })

          timeline
            .to(
              logo[1],
              {
                opacity: 0,
                yPercent: -100
              },
              `+=${timelineDelay}`
            )
            .to(
              logo[6],
              {
                yPercent: -100
              },
              '<'
            )
            .set(logo[1], {
              yPercent: 100,
              opacity: 1
            })

          timeline
            .to(
              logo[3],
              {
                opacity: 0,
                yPercent: -100
              },
              `+=${timelineDelay}`
            )
            .to(
              logo[8],
              {
                yPercent: -100
              },
              '<'
            )
            .set(logo[3], {
              yPercent: 100,
              opacity: 1
            })

          timeline
            .to(
              logo[4],
              {
                opacity: 0,
                yPercent: -100
              },
              `+=${timelineDelay}`
            )
            .to(
              logo[9],
              {
                yPercent: -100
              },
              '<'
            )
            .set(logo[4], {
              yPercent: 100,
              opacity: 1
            })

          timeline
            .to(
              logo[6],
              {
                opacity: 0,
                yPercent: -200
              },
              `+=${timelineDelay}`
            )
            .to(
              logo[1],
              {
                yPercent: 0
              },
              '<'
            )
            .set(logo[6], {
              yPercent: 100,
              opacity: 1
            })

          timeline
            .to(
              logo[7],
              {
                opacity: 0,
                yPercent: -200
              },
              `+=${timelineDelay}`
            )
            .to(
              logo[2],
              {
                yPercent: 0
              },
              '<'
            )
            .set(logo[7], {
              yPercent: 100,
              opacity: 1
            })

          timeline
            .to(
              logo[5],
              {
                opacity: 0,
                yPercent: -200
              },
              `+=${timelineDelay}`
            )
            .to(
              logo[0],
              {
                yPercent: 0
              },
              '<'
            )
            .set(logo[5], {
              yPercent: 100,
              opacity: 1
            })

          timeline
            .to(
              logo[9],
              {
                opacity: 0,
                yPercent: -200
              },
              `+=${timelineDelay}`
            )
            .to(
              logo[4],
              {
                yPercent: 0
              },
              '<'
            )
            .set(logo[9], {
              yPercent: 100,
              opacity: 1
            })

          timeline
            .to(
              logo[8],
              {
                opacity: 0,
                yPercent: -200
              },
              `+=${timelineDelay}`
            )
            .to(
              logo[3],
              {
                yPercent: 0
              },
              '<'
            )
            .set(logo[8], {
              yPercent: 100,
              opacity: 1
            })
        }
      }

      timeline = gsap.timeline({
        id: 'clients',
        delay: timelineDelay,
        defaults: {
          duration: 1,
          ease: Power4.easeInOut
        },
        onComplete: () => {
          gsap.delayedCall(0.5, animateLogos)
        }
      })

      setTimeout(() => {
        animateLogos()
      }, timelineDelay * 3 * 1000)
    }

    return () => {
      if (timeline) {
        timeline.clear()
        timeline.kill()
        timeline = null
      }

      window.removeEventListener('resize', resizeListener)
    }
  }, [data.outlogoimage, isNotDesktop])

  return (
    <Box
      sx={{
        pt: { xs: 5.5, mobile: 11, laptop: 12 }
      }}>
      <Box
        sx={{
          maxWidth: 1152,
          mx: 'auto',
          pb: { xs: 5.5, mobile: 11, laptop: 12 },
          px: {
            xs: 3,
            mobile: 8,
            laptop: 4,
            desktop: 0
          }
        }}>
        <Typography
          variant='h1'
          component='h2'
          sx={{ fontSize: { laptop: 54 }, textAlign: 'center' }}>
          {data.customHeaderText || data.ourlogotitle}
        </Typography>

        <List
          disablePadding
          sx={{
            height: { laptop: 100 },
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            mt: 5,
            overflow: 'hidden',
            rowGap: { xs: 5, laptop: 0 }
          }}>
          {data.outlogoimage?.map((logoimage, index) => (
            <ListItem
              disablePadding
              key={logoimage._key}
              ref={(ref: never) => {
                logoRefs.current[index] = ref
              }}
              sx={{
                width: { xs: '50%', laptop: '20%' },
                height: '100%',
                alignItems: 'center',
                display: 'flex',
                gap: 2.5,
                justifyContent: 'center',
                img: {
                  width: 'auto',
                  height: '100%',
                  maxWidth: '70%',
                  objectFit: 'contain'
                }
              }}>
              <Image props={logoimage?.asset?._ref} width='144' height='50' />
            </ListItem>
          ))}
        </List>
      </Box>

      <SectionDivider />
    </Box>
  )
}
